/* eslint-disable react-refresh/only-export-components --
 * Provider + its own hook are meant to be used together; splitting `useUnits`
 * into a second file for Fast Refresh granularity would just make the pair
 * harder to find. Same call as `components/ui/tabs.tsx`. */
import { createContext, useContext, useMemo, type ReactNode } from 'react'
import { useProfile } from '../api/hooks'
// units.ts is imported for TYPES ONLY. The dependency arrow is
// format.ts -> units.ts; this file sits above both.
import type { BodyWeightUnit, HeightStyle, HeightUnit, LoadUnit } from './units'
// The TOTAL wrappers from format.ts, never units.ts's raw converters: these are
// what call sites render, so they must cope with a null the API can return.
//
// Aliased on purpose. `bodyWeight` and `height` are local consts below (the
// units) that would otherwise collide with format.ts's `bodyWeight` and this
// module's own `Units.height` field name; importing under the same name would
// shadow the local and, e.g., `bodyWeight(n, bodyWeight)` would pass a
// function where a unit belongs. It type-errors rather than misrendering, but
// the error reads nothing like its cause — aliasing every import here avoids
// the trap outright rather than relying on remembering which names collide.
import {
  bodyWeight as fmtBodyWeight,
  bodyWeightParts as fmtBodyWeightParts,
  heightCm as fmtHeight,
  kg as fmtLoad,
  kgParts as fmtLoadParts,
  signedBodyWeight as fmtSignedBodyWeight,
  signedKg as fmtSignedLoad,
  tonnage as fmtTonnage,
  tonnageParts as fmtTonnageParts,
} from './format'

export interface Units {
  load: LoadUnit
  bodyWeight: BodyWeightUnit
  height: HeightUnit
  formatLoad: (kg: number | null | undefined) => string
  /**
   * `formatLoad`, split into a value and a trailing unit word — for a
   * set-grid cell or a hero metric rendered as two JSX nodes (Task 6). No
   * stone-style compound to worry about: a load always splits two ways.
   */
  formatLoadParts: (kg: number | null | undefined) => { value: string; unit: string }
  formatBodyWeight: (kg: number | null | undefined) => string
  formatHeight: (cm: number | null | undefined, style?: HeightStyle) => string
  tonnage: (kg: number | null | undefined) => string
  /** `tonnage`, split into a value and a trailing unit word — for a hero
   * figure rendered as two JSX nodes (Task 6). Thousands-grouped, unlike
   * `formatLoadParts` — see `tonnageParts`'s own doc comment in format.ts. */
  tonnageParts: (kg: number | null | undefined) => { value: string; unit: string }
  signedLoad: (kg: number | null | undefined) => string
  /** `signedKg`'s counterpart for a body-mass delta (Task 5). */
  signedBodyWeight: (kg: number | null | undefined) => string
  /**
   * `formatBodyWeight`, split into a value and a trailing unit word — for a
   * hero figure and a small dim unit rendered as two JSX nodes (Task 5). The
   * stone compound has no single trailing unit to split off, so `unit` comes
   * back `''` and the whole compound lives in `value`; render `unit` only
   * when it is non-empty.
   */
  formatBodyWeightParts: (kg: number | null | undefined) => { value: string; unit: string }
}

const METRIC: Pick<Units, 'load' | 'bodyWeight' | 'height'> = {
  load: 'kg',
  bodyWeight: 'kg',
  height: 'cm',
}

const UnitsContext = createContext<Units | null>(null)

export function UnitsProvider({ children }: { children: ReactNode }) {
  const { data } = useProfile()
  const value = useMemo<Units>(() => {
    // `data` is undefined until the profile query resolves — that state is
    // metric, same as a fresh account's column defaults, so a pending fetch
    // and a fresh account render identically rather than guessing.
    const load = data?.unit_load ?? METRIC.load
    const bodyWeight = data?.unit_body_weight ?? METRIC.bodyWeight
    const height = data?.unit_height ?? METRIC.height
    return {
      load,
      bodyWeight,
      height,
      formatLoad: (n) => fmtLoad(n, load),
      formatLoadParts: (n) => fmtLoadParts(n, load),
      formatBodyWeight: (n) => fmtBodyWeight(n, bodyWeight),
      formatHeight: (n, style) => fmtHeight(n, height, style),
      tonnage: (n) => fmtTonnage(n, load),
      tonnageParts: (n) => fmtTonnageParts(n, load),
      signedLoad: (n) => fmtSignedLoad(n, load),
      signedBodyWeight: (n) => fmtSignedBodyWeight(n, bodyWeight),
      formatBodyWeightParts: (n) => fmtBodyWeightParts(n, bodyWeight),
    }
  }, [data?.unit_load, data?.unit_body_weight, data?.unit_height])
  return <UnitsContext.Provider value={value}>{children}</UnitsContext.Provider>
}

/**
 * Bound formatters for the signed-in user's units.
 *
 * Throws outside a provider rather than defaulting to metric: a silent metric
 * fallback is indistinguishable from a correct render for the majority of
 * users, so the bug would only ever be reported by the minority it hurts.
 */
export function useUnits(): Units {
  const ctx = useContext(UnitsContext)
  if (!ctx) throw new Error('useUnits must be used inside <UnitsProvider>')
  return ctx
}
